/**
 * VO change-reason inheritance vs document display authority.
 *
 * Internal model (unchanged):
 *   resolvedReason = explicit override ?? VO-level reason
 *
 * Document / annotation display:
 *   displayItemReason = explicit override only (never inherited VO text)
 */

export const VO_REASON_REQUIRED_BEFORE_SEND =
  "Enter the Variation Order Reason before sending.";

/** Expected client validation: keep this inline and do not report it as a system error. */
export function variationOrderReasonSendError(value: unknown): string | null {
  return String(value ?? "").trim() ? null : VO_REASON_REQUIRED_BEFORE_SEND;
}

/**
 * Effective reason for internal inheritance: explicit override ?? VO-level reason.
 * Do not use this to decide whether an item-level Reason block renders.
 */
export function resolveVoEffectiveChangeReason(input: {
  lineChangeReason?: string | null;
  voChangeReason?: string | null;
}): string | null {
  const line = String(input.lineChangeReason ?? "").trim();
  if (line) return line.slice(0, 2000);
  const vo = String(input.voChangeReason ?? "").trim();
  return vo ? vo.slice(0, 2000) : null;
}

/**
 * Item-level Reason block authority for customer/staff documents and living-proposal
 * annotations.
 *
 * Returns the explicit component override only. When the stored line reason is
 * empty or merely duplicates the VO-level reason (inherited / legacy duplicate),
 * returns null so the item layout stays clean.
 */
export function resolveVoItemDisplayChangeReason(input: {
  lineChangeReason?: string | null;
  voChangeReason?: string | null;
}): string | null {
  const line = String(input.lineChangeReason ?? "").trim();
  if (!line) return null;
  const vo = String(input.voChangeReason ?? "").trim();
  if (vo && line === vo) return null;
  return line.slice(0, 2000);
}

/**
 * Rebuild explicit line overrides from persisted delta rows for legacy drafts that
 * predate the draft `changeReasons` map. Values that merely equal the VO header
 * are treated as inherited, not overrides.
 */
export function filterLegacyLineChangeReasonOverrides(
  lines: Array<{ stableLineKey: string; changeReason?: string | null }>,
  headerChangeReason: string | null,
): Record<string, string> {
  return Object.fromEntries(
    lines.flatMap((line) => {
      const display = resolveVoItemDisplayChangeReason({
        lineChangeReason: line.changeReason,
        voChangeReason: headerChangeReason,
      });
      return display ? [[line.stableLineKey, display]] : [];
    }),
  );
}
